import React, { useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useParams } from "react-router-dom";
import DashboardContent from "../../components/DashboardContent";
import useModalWindow from "../../hooks/useModalWindow";
import usePatientNarrativeDocuments from "../../hooks/usePatientNarrativeDocuments";
import { usePermissions } from "../../contexts/PermissionsContext";
import { NO_PERMISSION_TITLE, PERMISSIONS } from "../../utils/permissions";
import ProgressBar from "../../utils/ProgressBar";
import Loader from "../../utils/Loader";
import { FILE_UPLOAD_STATUS, PN_DOCUMENT_TYPE } from "../../utils/Constants";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../../components/RedirectHandler";
import { ToastContainer } from "react-toastify";
import { ReactComponent as DeleteFile } from "../../assets/images/icons/delete-small.svg";
import { ReactComponent as FileUploaded } from "../../assets/images/icons/file-uploaded.svg";
import { ReactComponent as FileProcessing } from "../../assets/images/icons/file-processing.svg";
import { ReactComponent as FileError } from "../../assets/images/icons/file-error.svg";
import { ReactComponent as FileProcessed } from "../../assets/images/icons/file-processed.svg";
import UploadStatusBar from "../../components/UploadStatusBar";

const ALLOWED_FILE_TYPES = ["DOCX", "RTF", "PDF"];

const PNFileUpload = () => {
  // Route param is named studyId (see AppRoutes.js), but for the PN app
  // this is a patient_narrative_id, not a Study id.
  const { studyId: patientNarrativeId } = useParams();
  const { modalWindow, handleCancel } = useModalWindow();
  const { hasPermission } = usePermissions();

  // pn:files:read fetches the list at all, pn:files:create covers adding
  // files (both the first upload and "Add more"), pn:files:delete the
  // per-file bin.
  const canReadFiles = hasPermission(PERMISSIONS.pnFilesRead);
  const canUploadFiles = hasPermission(PERMISSIONS.pnFilesCreate);
  const canDeleteFiles = hasPermission(PERMISSIONS.pnFilesDelete);

  const {
    documents,
    documentsByType,
    upload,
    remove,
    wrongEnvironment: documentsWrongEnvironment,
  } = usePatientNarrativeDocuments(patientNarrativeId, {
    canRead: canReadFiles,
  });

  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);
  const [listingProgress, setListingProgress] = useState({ val: [] });
  const [ciomsProgress, setCiomsProgress] = useState({ val: [] });
  const errRef = useRef();
  const listingProgressRef = useRef({ val: [] });
  const ciomsProgressRef = useRef({ val: [] });
  const documentTypeRef = useRef("");

  const listingDocuments = documentsByType[PN_DOCUMENT_TYPE.listing] || [];
  const ciomsDocuments = documentsByType[PN_DOCUMENT_TYPE.cioms] || [];

  function isIterable(obj) {
    if (obj == null) {
      return false;
    }
    return typeof obj[Symbol.iterator] === "function";
  }

  const handleUploadError = error => {
    if (isProduction) {
      Sentry.captureException(error);
    }
    if (!error?.response) {
      // setErrMsg("No Server Response");
    } else if (error.response?.status === 401) {
      setErrMsg("Invalid user credentials");
    } else if (error.response?.status === 403) {
      setShowRedirect(true);
    } else {
      setErrMsg("Failed to upload document");
    }
    errRef.current?.focus();
  };

  const runUpload = (idx, file, docType) => {
    const listingVal = listingProgressRef?.current?.val;
    let _listingProgress = isIterable(listingVal) ? [...listingVal] : [];
    const ciomsVal = ciomsProgressRef?.current?.val;
    let _ciomsProgress = isIterable(ciomsVal) ? [...ciomsVal] : [];

    return upload(file, docType, event => {
      if (docType === PN_DOCUMENT_TYPE.listing) {
        _listingProgress[idx].percentage = Math.round(
          (100 * event.loaded) / event.total
        );
        setListingProgress({ val: _listingProgress });
      } else if (docType === PN_DOCUMENT_TYPE.cioms) {
        _ciomsProgress[idx].percentage = Math.round(
          (100 * event.loaded) / event.total
        );
        setCiomsProgress({ val: _ciomsProgress });
      }
    })
      .then(() => {
        if (docType === PN_DOCUMENT_TYPE.listing) {
          _listingProgress[idx].percentage = -1;
          setListingProgress({ val: _listingProgress });
        } else if (docType === PN_DOCUMENT_TYPE.cioms) {
          _ciomsProgress[idx].percentage = -1;
          setCiomsProgress({ val: _ciomsProgress });
        }
      })
      .catch(error => {
        if (docType === PN_DOCUMENT_TYPE.listing) {
          _listingProgress[idx].percentage = 0;
          setListingProgress({ val: _listingProgress });
        } else if (docType === PN_DOCUMENT_TYPE.cioms) {
          _ciomsProgress[idx].percentage = 0;
          setCiomsProgress({ val: _ciomsProgress });
        }
        handleUploadError(error);
      });
  };

  const handleChange = file => {
    if (!canUploadFiles) return;

    // A fresh upload attempt supersedes whatever the last one reported, so
    // the previous failure is cleared here rather than being left on screen
    // next to a file that uploaded fine. If this attempt fails too,
    // runUpload's catch puts the message back.
    setErrMsg("");

    const docType = documentTypeRef.current;

    let files = [];
    if (file instanceof File) {
      files[0] = file;
    } else {
      files = Array.from(file);
    }

    let _progressInfos = files.map(file => ({
      percentage: 0,
      fileName: file.name,
    }));

    if (docType === PN_DOCUMENT_TYPE.listing) {
      listingProgressRef.current = { val: _progressInfos };
    } else if (docType === PN_DOCUMENT_TYPE.cioms) {
      ciomsProgressRef.current = { val: _progressInfos };
    }

    const uploadPromises = files.map((file, i) => runUpload(i, file, docType));

    Promise.all(uploadPromises).then(() => {
      setListingProgress({ val: [] });
      setCiomsProgress({ val: [] });
    });

    handleCancel();
  };

  const handleUploadPopup = docType => {
    if (!canUploadFiles) return;

    documentTypeRef.current = docType;

    let displayName =
      docType === PN_DOCUMENT_TYPE.listing ? "Listing" : "CIOMS";

    modalWindow({
      title: "Add " + displayName + " files",
      message: (
        <FileUploader
          handleChange={handleChange}
          name="file"
          types={ALLOWED_FILE_TYPES}
          label={"Upload or Drop your files here to upload"}
          classes={"drop_area"}
          multiple={true}
        />
      ),
      confirmButtonLabel: "",
    });
  };

  const handleFileDelete = documentId => {
    if (!canDeleteFiles) return;

    remove(documentId).catch(handleUploadError);

    handleCancel();
  };

  const ListingProgressCode = () => {
    if (
      listingDocuments?.length > 0 ||
      (listingProgress && listingProgress?.val?.length > 0)
    ) {
      return (
        <div>
          <label className="file-upload-label">Add Listing</label>
          <div className={"csr-file-upload__component-uploaded"}>
            <div className={"csr-file-upload__component-uploaded__left"}>
              {listingDocuments?.map(doc => (
                <div
                  key={doc.document_id}
                  className={"csr-file-upload__component-uploaded__doc"}>
                  {doc.status === FILE_UPLOAD_STATUS.processing ? (
                    <FileProcessing />
                  ) : doc.status === FILE_UPLOAD_STATUS.processed ? (
                    <FileProcessed />
                  ) : doc.status === FILE_UPLOAD_STATUS.errored ? (
                    <FileError />
                  ) : (
                    <FileUploaded />
                  )}

                  <label>{doc.filename}</label>
                  <button
                    className={"delete"}
                    disabled={!canDeleteFiles}
                    title={canDeleteFiles ? undefined : NO_PERMISSION_TITLE}
                    onClick={() => handleFileDelete(doc.document_id)}>
                    <DeleteFile />
                  </button>
                </div>
              ))}

              {listingProgress &&
                listingProgress?.val?.length > 0 &&
                listingProgress?.val?.map((info, index) => (
                  <div
                    key={"listing-" + index}
                    className={`csr-file-upload__component-uploading${info.percentage === -1 ? " hide" : ""}`}>
                    <label>{info.fileName}</label>
                    <div className={"progress-bar__wrapper"}>
                      <Loader loading={true} />
                      <ProgressBar completed={info.percentage} />
                    </div>
                  </div>
                ))}
            </div>
            <div className={"csr-file-upload__component-uploaded__right"}>
              <button
                className={
                  "input-submit input-submit__tertiary input-submit__small"
                }
                disabled={!canUploadFiles}
                title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
                onClick={() => handleUploadPopup(PN_DOCUMENT_TYPE.listing)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px" }}>
                  <path
                    d="M8 10L8 2M8 2L5 5M8 2L11 5M2 12L14 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add more Listing files
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={"csr-file-upload__component"}>
          <label>Add Listing</label>
          <button
            className={"input-submit input-submit__primary input-submit__small"}
            disabled={!canUploadFiles}
            title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
            onClick={() => handleUploadPopup(PN_DOCUMENT_TYPE.listing)}>
            Add Listing
          </button>
        </div>
      );
    }
  };

  const CiomsProgressCode = () => {
    if (
      ciomsDocuments?.length > 0 ||
      (ciomsProgress && ciomsProgress?.val?.length > 0)
    ) {
      return (
        <div>
          <label className="file-upload-label">Add CIOMS</label>
          <div className={"csr-file-upload__component-uploaded"}>
            <div className={"csr-file-upload__component-uploaded__left"}>
              {ciomsDocuments?.map(doc => (
                <div
                  key={doc.document_id}
                  className={"csr-file-upload__component-uploaded__doc"}>
                  {doc.status === FILE_UPLOAD_STATUS.processing ? (
                    <FileProcessing />
                  ) : doc.status === FILE_UPLOAD_STATUS.processed ? (
                    <FileProcessed />
                  ) : doc.status === FILE_UPLOAD_STATUS.errored ? (
                    <FileError />
                  ) : (
                    <FileUploaded />
                  )}

                  <label>{doc.filename}</label>
                  <button
                    className={"delete"}
                    disabled={!canDeleteFiles}
                    title={canDeleteFiles ? undefined : NO_PERMISSION_TITLE}
                    onClick={() => handleFileDelete(doc.document_id)}>
                    <DeleteFile />
                  </button>
                </div>
              ))}

              {ciomsProgress &&
                ciomsProgress?.val?.length > 0 &&
                ciomsProgress?.val?.map((info, index) => (
                  <div
                    key={"cioms-" + index}
                    className={`csr-file-upload__component-uploading${info.percentage === -1 ? " hide" : ""}`}>
                    <label>{info.fileName}</label>
                    <div className={"progress-bar__wrapper"}>
                      <Loader loading={true} />
                      <ProgressBar completed={info.percentage} />
                    </div>
                  </div>
                ))}
            </div>
            <div className={"csr-file-upload__component-uploaded__right"}>
              <button
                className={
                  "input-submit input-submit__tertiary input-submit__small"
                }
                disabled={!canUploadFiles}
                title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
                onClick={() => handleUploadPopup(PN_DOCUMENT_TYPE.cioms)}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px" }}>
                  <path
                    d="M8 10L8 2M8 2L5 5M8 2L11 5M2 12L14 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Add more CIOMS files
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={"csr-file-upload__component"}>
          <label>Add CIOMS</label>
          <button
            className={"input-submit input-submit__primary input-submit__small"}
            disabled={!canUploadFiles}
            title={canUploadFiles ? undefined : NO_PERMISSION_TITLE}
            onClick={() => handleUploadPopup(PN_DOCUMENT_TYPE.cioms)}>
            Add CIOMS
          </button>
        </div>
      );
    }
  };

  const PageContent = () => {
    return (
      <div className={"csr-file-upload csr-file-upload__wrapper"}>
        {/* Driven by the GET /patient-narratives/{id}/documents response
            alone, so the counts are the backend's view of these files: a
            failed upload never reaches the list, and a document that later
            moves to PROCESSED/ERROR is picked up by the hook's polling. */}
        <UploadStatusBar documents={documents} />

        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>

        {ListingProgressCode()}
        {CiomsProgressCode()}
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || documentsWrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Upload your Listing and CIOMS files"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Patient Narrative Name",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/summary",
      }}
      nextButton={{
        buttonTitle: "Project & Conventions",
        buttonGoToAction: "/pn/" + patientNarrativeId + "/project-conventions",
      }}
    />
  );
};

export default PNFileUpload;
